import * as React from 'react';
import { Check, ChevronDown, UserRound } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Input } from './input';

/**
 * PersonLookup — pick someone from the organisation, or just type a name.
 *
 * Allocations usually go to a staff member, but not always: a director's
 * partner or a contractor has to be recordable without first creating them a
 * login. Typing anything that isn't a match is a valid answer, kept as free
 * text with no user attached.
 *
 * value: { userId, name } — userId is null for a free-text person.
 */
function PersonLookup({ value, onChange, users = [], placeholder = 'Search staff or type a name', disabled = false, id }) {
  const [term, setTerm] = React.useState(value?.name || '');
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);

  // Keep the field in step when the form loads or is reset elsewhere.
  React.useEffect(() => {
    setTerm(value?.name || '');
  }, [value?.name]);

  React.useEffect(() => {
    const onDocumentClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, []);

  const normalised = React.useMemo(
    () =>
      users
        .map((user) => ({
          userId: user.userId || user.$id || user.id,
          name:
            user.name ||
            user.userName ||
            `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim(),
          email: user.email || user.userEmail || ''
        }))
        .filter((user) => user.userId && (user.name || user.email)),
    [users]
  );

  const matches = React.useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return normalised;
    return normalised.filter(
      (user) => user.name.toLowerCase().includes(needle) || user.email.toLowerCase().includes(needle)
    );
  }, [normalised, term]);

  const exactMatch = matches.some((user) => user.name.toLowerCase() === term.trim().toLowerCase());
  const canUseFreeText = term.trim().length > 0 && !exactMatch;

  const choose = (person) => {
    onChange({ userId: person.userId, name: person.name });
    setTerm(person.name);
    setOpen(false);
  };

  const applyFreeText = () => {
    onChange({ userId: null, name: term.trim() });
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Input
          id={id}
          value={term}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
            // Typing detaches from the chosen user until something is picked again.
            onChange({ userId: null, name: e.target.value });
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canUseFreeText) {
              e.preventDefault();
              applyFreeText();
            }
            if (e.key === 'Escape') setOpen(false);
          }}
          className="pr-8"
        />
        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
      </div>

      {value?.userId && !open && (
        <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
          <UserRound className="h-3 w-3" /> Staff member
        </p>
      )}

      {open && !disabled && (matches.length > 0 || canUseFreeText) && (
        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border bg-popover p-1 shadow-md">
          {matches.map((person) => (
            <button
              key={person.userId}
              type="button"
              onClick={() => choose(person)}
              className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
            >
              <span className="min-w-0">
                <span className="block truncate">{person.name || person.email}</span>
                {person.email && person.name && (
                  <span className="block truncate text-xs text-muted-foreground">{person.email}</span>
                )}
              </span>
              {value?.userId === person.userId && <Check className="h-4 w-4 shrink-0" />}
            </button>
          ))}

          {canUseFreeText && (
            <button
              type="button"
              onClick={applyFreeText}
              className={cn(
                'w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent',
                matches.length > 0 && 'mt-1 border-t pt-2'
              )}
            >
              Use “<span className="font-medium">{term.trim()}</span>” as a name
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export { PersonLookup };
